import {Directive, HostListener, signal, ViewChild} from '@angular/core';
import { AbsoluteScrollStrategy, AutoPositionStrategy, CloseScrollStrategy, ColumnPinningPosition, ConnectedPositioningStrategy, GlobalPositionStrategy, GridSummaryCalculationMode, HorizontalAlignment, ISortingOptions, NoOpScrollStrategy, PositionSettings, VerticalAlignment } from '@infragistics/igniteui-angular/core';
import { GridSelectionMode, GridSummaryPosition, IGridStateOptions, IPinningConfig, RowPinningPosition } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { RadioGroupAlignment } from '@infragistics/igniteui-angular/radio';
import { SplitterType } from '@infragistics/igniteui-angular/splitter';
import {scaleInCenter, scaleOutCenter, slideInBottom, slideOutTop} from '@infragistics/igniteui-angular/animations';
import {useAnimation} from '@angular/animations';
import {ClsUtil} from './cls-util';
import {PasteHandlerNeodataDirective} from '../core/directivas/paste-handler-neodata.directive';
import { environment } from '../../environments/environment';

@Directive()
export class ClsConfig {
  @ViewChild(PasteHandlerNeodataDirective) public pasteHandlerNeo: PasteHandlerNeodataDirective;

  protected readonly gTituloSistema = signal('NEODATA PU NUBE+');
  protected readonly gVersionSistema = signal('26.1.0 - XX/05/2026');

  public keyStimulsoft: string;

  /** Locale BCP-47 para Ignite (`igx-grid`, date pickers, etc.); siempre alineado con `localStorage` vía `ClsUtil`. */
  public get localGrid(): string {
    return ClsUtil.obtenerLocalGrid();
  }
  public optionsGrid: IGridStateOptions = {
    cellSelection: true,
    rowSelection: false,
    filtering: true,
    advancedFiltering: true,
    paging: true,
    sorting: true,
    groupBy: true,
    columns: true,
    expansion: true,
    rowPinning: false,
    columnSelection: true,
  };
  public gNomPrefixNeodata = 'NEODATA';
  public gNomPrefixPptoPI = 'PIEDEINDIRECTOS';
  public gNomPrefixPptoSC = 'SOBRECOSTO';
  public gNomPrefixPptoCH = 'COSTOHORARIO';
  public gNomPrefixPptoFASAR = 'FASAR';
  public gNomPrefixOrgLogos = 'LOGO-REPORTES';
  public gNomBocketPI: string;
  public gNomBocketSC: string;
  public gNomBocketCH: string;
  public gNomBocketFASAR: string;
  public gNomBocketPpto: string;
  public gNomBocketOrgLogo: string;
  public gPrefixMoneda1 = '-Moneda1';
  public gPrefixMoneda2 = '-Moneda2';
  public gNomDocCHDefault = 'EstandarCostoHorario.xlsx';
  public gNomDocPIDefault = 'Indirecto Unico.xlsx';
  public gNomLogoDefault = 'neodata.png';
  // default 30,000 registros, en PU 60,000
  public gLimiteRegistro: number;
  public gUrlProyecto: string;
  public gUrlCloudRunLogin: string;
  public gUrlCloudRunPpto: string;
  public gUrlCloudRunNET: string;
  public gUrlNodeJSAI: string;
  public gRegexUrl: RegExp = RegExp(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/);
  public gRegexCorreos = '^[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$';
  public gRegexCodAut = /\?$/;
  // estilo celda
  public cellStyles = {
    background: 'var(--neo-blocked-cell-background)',
    color: 'var(--neo-blocked-cell-color)'
  };
  // public cellStyles: any;
  // FORMATOS
  public formatDecImporte: any;
  public formatDecCantidad: any;
  public formatDecExpIns: any;
  public formatPorcentaje2Dec = {
    digitsInfo: '1.2-2',
    currencyCode: 'MXN',
    local: ClsUtil.obtenerLocalGrid()
  };
  public formatPorcentaje4Dec = {
    digitsInfo: '1.4-4',
    currencyCode: 'MXN',
    local: ClsUtil.obtenerLocalGrid()
  };

  public formatColMoneda2Dec = {
    digitsInfo: '1.2-2',
    currencyCode: 'MXN',
    display: 'symbol-narrow',
    local: ClsUtil.obtenerLocalGrid()
  };

  public formatColCantidad6Dec = {
    digitsInfo: '1.6-6',
    currencyCode: 'MXN',
    local: ClsUtil.obtenerLocalGrid()
  };

  public formatColFechaDDMMYYYY = {
    format: 'dd/MM/yyyy',
    timezone: 'GMT',
    local: ClsUtil.obtenerLocalGrid()
  };
  // OPCIONES DE COPIADO
  public opcionesCopiado = {
    enabled: true,
    copyHeaders: false,
    copyFormatters: true,
    separator: '\t'
  };
  // CONFIGURACION RADIOGROUP
  public alignmentRadioH = RadioGroupAlignment.horizontal;
  public alignmentRadioV = RadioGroupAlignment.vertical;

  // CONFIGURACION TIPO SPLITTER
  public typeHorizontal = SplitterType.Horizontal;
  public typeVertical = SplitterType.Vertical;

  // CONFIGURACION GRID PINNING
  public pinConfigColStartRowTop: IPinningConfig = {columns: ColumnPinningPosition.Start, rows: RowPinningPosition.Top};
  public pinConfigColEnd: IPinningConfig = {columns: ColumnPinningPosition.End};
  public pinConfigColStart: IPinningConfig = {columns: ColumnPinningPosition.Start};

  // CONFIGURACION GRID SUMMARY
  public summaryCalculationModeRootLevelOnly: GridSummaryCalculationMode = GridSummaryCalculationMode.rootLevelOnly;
  public summaryCalculationModeRootAndChildLevels: GridSummaryCalculationMode = GridSummaryCalculationMode.rootAndChildLevels;
  public summaryCalculationModeChildLevelsOnly: GridSummaryCalculationMode = GridSummaryCalculationMode.childLevelsOnly;

  public summaryPositionTop: GridSummaryPosition = GridSummaryPosition.top;
  public summaryPositionBottom: GridSummaryPosition = GridSummaryPosition.bottom;

  // CONFIGURACION GRID SORTING
  public sortingOptions: ISortingOptions = {mode: 'single'};
  public sortingOptionsMultiple: ISortingOptions = {mode: 'multiple'};
  // CONFIGURACION GRID ROW SELCCIONT
  public rowSelectionMultiple: GridSelectionMode = 'multiple';
  public rowSelectionNone: GridSelectionMode = 'none';
  public rowSelectionSingle: GridSelectionMode = 'single';
  public rowSelectionMultipleCascade: GridSelectionMode = 'multipleCascade';

  public positionStrategyScaleCenter = new GlobalPositionStrategy({
    openAnimation: scaleInCenter,
    closeAnimation: scaleOutCenter
  });
  /**
   * positionStrategy: this.positionStrategyScaleCenter = new GlobalPositionStrategy({
   *     openAnimation: scaleInCenter,
   *     closeAnimation: scaleOutCenter
   *   });,
   * scrollStrategy: new AbsoluteScrollStrategy(),
   * modal: true,
   * closeOnEscape: true
   */
  public overlaySettingsScaleCenterModal = {
    positionStrategy: this.positionStrategyScaleCenter,
    scrollStrategy: new AbsoluteScrollStrategy(),
    modal: true,
    closeOnEscape: true
  };
  /**
   * horizontalDirection: HorizontalAlignment.Center,
   * horizontalStartPoint: HorizontalAlignment.Center,
   * verticalStartPoint: VerticalAlignment.Bottom
   */
  public overlaySettingsCenterBottom = {
    positionStrategy: new ConnectedPositioningStrategy({
      horizontalDirection: HorizontalAlignment.Center,
      horizontalStartPoint: HorizontalAlignment.Center,
      verticalStartPoint: VerticalAlignment.Bottom
    }),
    scrollStrategy: new NoOpScrollStrategy()
  };
  /**
   * horizontalDirection: HorizontalAlignment.Left,
   * horizontalStartPoint: HorizontalAlignment.Right,
   * verticalStartPoint: VerticalAlignment.Bottom
   */
  public overlaySettingsLeftRightBottom = {
    positionStrategy: new ConnectedPositioningStrategy({
      horizontalDirection: HorizontalAlignment.Left,
      horizontalStartPoint: HorizontalAlignment.Right,
      verticalStartPoint: VerticalAlignment.Bottom
    }),
    scrollStrategy: new NoOpScrollStrategy(),
    modal: false,
    closeOnEscape: true
  };
  /**
   * horizontalDirection: HorizontalAlignment.Left,
   * horizontalStartPoint: HorizontalAlignment.Right,
   * verticalStartPoint: VerticalAlignment.Top,
   * scrollStrategy: new NoOpScrollStrategy()
   */
  public overlaySettingsLeftRightTop = {
    positionStrategy: new ConnectedPositioningStrategy({
      horizontalDirection: HorizontalAlignment.Left,
      horizontalStartPoint: HorizontalAlignment.Right,
      verticalStartPoint: VerticalAlignment.Top,
      verticalDirection: VerticalAlignment.Top
    }),
    scrollStrategy: new NoOpScrollStrategy(),
    modal: false,
    closeOnEscape: true
  };

  /**
   * horizontalDirection: HorizontalAlignment.Right,
   * horizontalStartPoint: HorizontalAlignment.Right,
   * verticalStartPoint: VerticalAlignment.Middle,
   * verticalDirection: VerticalAlignment.Middle
   * scrollStrategy: new NoOpScrollStrategy()
   */
  public overlaySettingsRight = {
    positionStrategy: new ConnectedPositioningStrategy({
      horizontalDirection: HorizontalAlignment.Right,
      horizontalStartPoint: HorizontalAlignment.Right,
      verticalStartPoint: VerticalAlignment.Middle,
      verticalDirection: VerticalAlignment.Middle
    }),
    scrollStrategy: new NoOpScrollStrategy(),
    modal: false,
    closeOnEscape: true
  };

  /**
   * horizontalDirection: HorizontalAlignment.Left,
   * horizontalStartPoint: HorizontalAlignment.Left,
   * verticalStartPoint: VerticalAlignment.Middle,
   * verticalDirection: VerticalAlignment.Middle
   * scrollStrategy: new NoOpScrollStrategy()
   */
  public overlaySettingsLeft = {
    positionStrategy: new ConnectedPositioningStrategy({
      horizontalDirection: HorizontalAlignment.Left,
      horizontalStartPoint: HorizontalAlignment.Left,
      verticalStartPoint: VerticalAlignment.Middle,
      verticalDirection: VerticalAlignment.Middle
    }),
    scrollStrategy: new NoOpScrollStrategy(),
    modal: false,
    closeOnEscape: true
  };

  //#region ANCHO Y ALTO PARA EL GRID CON Y SIN DRAWER
  public altoGrd = '400px';
  public altoGrdPartida = '400px';
  public anchoGrd = '400px';
  public altoWinHeight = 0;
  public isMobileWidth: boolean = false;
  public isMobileHeight: boolean = false;
  public margenAncho = 12;
  public margenAlto = 12;
  public margenAltoPartida = 12;
  //#endregion

  public positionStrategyAuto = new AutoPositionStrategy();

  public overlaySettingsAuto = {
    positionStrategy: this.positionStrategyAuto,
    scrollStrategy: new AbsoluteScrollStrategy(),
    modal: false,
    closeOnEscape: false
  };

  public positionSettingsOnOpen: PositionSettings = {
    openAnimation: useAnimation(slideInBottom, {params: {fromPosition: 'translateY(100%)'}}),
    closeAnimation: useAnimation(slideOutTop, {params: {toPosition: 'translateY(-100%)'}}),
    minSize: {height: 300, width: 300}
  };

  public dialogOverlaySettings = {
    modal: true,
    scrollStrategy: new CloseScrollStrategy(),
    positionStrategy: new GlobalPositionStrategy(this.positionSettingsOnOpen),
    closeOnOutsideClick: false,
    closeOnEscape: false
  };

  constructor() {
    this.gLimiteRegistro = 30000;
    this.keyStimulsoft = '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHlkLiP2rNzCI8VOG3H9pnGDrzXYxFOXC2KgL7/RBI5TtX0p8Jl24yKAhlQ9k2tHxBrcD35AgOVWRnC6UYvmjnsGbigMgf/7LNv4iN12cZQ/fKdrB2TPw21CFEEWzOC/4A334Hk9oTyqmBePNPBVu7rRwTOnfqS9niaBqMYsrPavq8nmgLczNSlAMm2sdZAskxr6O6AjmpLS7PZhQd1wb4NK+wIoEd8Gw/TN0Zjmot+H7dKHkG74EqATpynOi9ekyVQk6bvOVCvrB+HMoWaVMCg5JiJ5eJ1JaMRJt2kddsmTfckTewN0wi60vS5I8Q0cCzu6h3By3FQPZaNN/++xFzmOGrpcG9a2pt0NHrlE+giDnsQ1RcihXCJ0M6+aIxKplN0nGNQTqlnadXuCfSFFGnnvtYh+ebRAQ/PXncRcyNCM5urYRgq6PGQpH/SpzVhQKw1YqUu/VkHoF1h5MX+bX9QdT2KhxHF8rxb64xYjWJAWkkw7ytg0XzwauFg5PAOmFoJzQR16ODIUyywlpeH6Jh2B';

    // -------------- URL CLOUD RUN LOGIN --------------------
    this.gUrlCloudRunLogin = environment.urlCloudRunLogin;

    // -------------- URL CLOUD RUN PPTO --------------------
    this.gUrlCloudRunPpto = environment.urlCloudRunPpto;

    // -------------- URL CLOUD RUN NET --------------------
    this.gUrlCloudRunNET = environment.urlCloudRunNET;

    // -------------- URL NODEJS IA --------------------
    this.gUrlNodeJSAI = environment.urlNodeJSAI;

    // -------------- ORGANIZACION LOGOS --------------------
    this.gNomBocketOrgLogo = environment.nomBocketOrgLogo;

    // -------------- PIE DE INDIRECTOS --------------------
    this.gNomBocketPI = environment.nomBocketPI;

    // -------------- COSTO HORARIO ------------------------
    this.gNomBocketCH = environment.nomBocketCH;

    // -------------- FASAR ------------------------
    this.gNomBocketFASAR = environment.nomBocketFASAR;

    // -------------- SOBRECOSTO ------------------------
    this.gNomBocketSC= environment.nomBocketSC;

    // -------------- PPTO ---------------------------------
    this.gNomBocketPpto = environment.nomBocketPpto;

    // -------------- RUTA PROYECTO ---------------------------------
    this.gUrlProyecto = environment.urlProyecto;
  }

  calcularAlto(): void {
    this.altoGrd = window.innerHeight - this.margenAlto + 'px';
  }

  calcularAltoPartida(): void {
    this.altoGrdPartida = window.innerHeight - this.margenAltoPartida + 'px';
  }

  calcularAncho(): void {
    this.anchoGrd = (window.innerWidth - this.margenAncho) + 'px';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    setTimeout(() => {
      this.anchoGrd = (window.innerWidth - this.margenAncho) + 'px';
      this.altoGrd = (window.innerHeight - this.margenAlto) + 'px';
      this.altoGrdPartida = (window.innerHeight - this.margenAltoPartida) + 'px';
      this.altoWinHeight = window.innerHeight;
      // Detecta si el ancho de la ventana es menor a un tamaño típico de pantalla de escritorio
      this.isMobileWidth = window.innerWidth < 1400;
      this.isMobileHeight = window.innerHeight < 700;
    }, 500);
  }

  /**
   * Metodo que da focus a la primer columna editable del grid
   * @param row fila que va a editar
   */
  public startEdit(row?: any): void {
    const grid = row.grid;
    grid.cdr.detectChanges();
    const indexCol = row.columns.findIndex((cell: any) => cell.editable);
    grid.navigateTo(row.index, indexCol, (args: any) => {
      args.target.activate();
      grid.cdr.detectChanges();
      const firstEditable = row.cells.filter((cell: any) => cell.editable)[0];
      if (grid.rowList.filter((r: any) => r === row).length !== 0) {
        grid.gridAPI.crudService.enterEditMode(firstEditable);
        firstEditable.activate(null);
      }
    });
  }

  /**
   * Metodo que da focus a la columna indica, hay que tomar en cuenta que debe ser editable tal columna
   * @param row fila que va a editar
   * @param columna es el campo donde dara focus para comenzar la edicion
   */
  public startEditPorColumna(row?: any, columna?: any): void {
    const grid = row.grid;
    grid.cdr.detectChanges();
    const indexCol = row.columns.findIndex((cell: any) => cell._field === columna);
    grid.navigateTo(row.index, indexCol, (args: any) => {
      args.target.activate();
      grid.cdr.detectChanges();
      const firstEditable = row.cells.filter((cell: any) => cell.column._field === columna)[0];
      if (grid.rowList.filter((r: any) => r === row).length !== 0) {
        grid.gridAPI.crudService.enterEditMode(firstEditable);
        firstEditable.activate(null);
      }
    });
  }

  public beginAddRowByIndexNeo(grid: IgxGridComponent, rowContextIndex: number) {
    grid.cdr.detectChanges();
    const indexCol = grid.columns.findIndex((cell: any) => cell.editable);
    grid.navigateTo(rowContextIndex, indexCol, (args: any) => {
      args.target.activate();
      grid.cdr.detectChanges();
      grid.beginAddRowByIndex(rowContextIndex);
    });
  }

  public stopPropagationPegado(eventArgs: any) {
    const ctrl = eventArgs.ctrlKey;
    const key = eventArgs.keyCode;
    // Ctrl-V || Shift-Ins || Cmd-V
    if ((ctrl || eventArgs.metaKey) && key === 86 || eventArgs.shiftKey && key === 45) {
      eventArgs.stopPropagation();
    }
    // Ctrl-C || Shift-Ins || Cmd-C
    if ((ctrl || eventArgs.metaKey) && !eventArgs.shiftKey && key === 67) {
      eventArgs.stopPropagation();
    }
  }

  //#endregion
}
